import React, { FC, useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { Tick } from 'common/svg';
import { Divider } from 'common/components';
import { ICity } from 'entities/City/City.models';
import { AddressSuggestionSelector } from 'entities/Address/components';
import { IAddressSuggestion } from 'entities/Address/Address.models';

interface IListItem {
  item: ICity;
  isSelected: boolean;
  isDetected: boolean;
  onSelect: (id: number) => void;
}

const ListItem: FC<IListItem> = ({ item, isSelected, isDetected, onSelect }) => {
  const handleSelect = (): void => {
    onSelect(item.id);
  };

  return (
    <div className={`p-3 cursor-pointer select-city-modal-list-item ${isSelected ? 'active' : ''}`} onClick={handleSelect}>
      <div className="flex align-center gap-3">
        {isSelected && <Tick className="icon-bright-green" />}

        <div className="ff-regular fs-s" style={{ lineHeight: '20px' }}>
          {item.name}
        </div>
      </div>

      {isDetected && <div className="mt-2 ff-regular fs-xs color-bright-green">Ваше местоположение</div>}

      {isSelected && (
        <div className="mt-2 ff-regular fs-xs color-dark-grey">
          {`Сроки доставки для этого города будут рассчитаны от ближайшего города, в котором есть склад (${item.parent ? item.parent.name : item.name}).`}
        </div>
      )}
    </div>
  );
};

interface IComponentProps {
  open: boolean;
  loading: boolean;
  allowCancel?: boolean;
  items: ICity[];
  userCity?: ICity;
  detectedCityId?: number;
  onCancel: () => void;
  onSubmit: (id: number, onSuccess: () => void) => void;
}

export const SelectCityModal: FC<IComponentProps> = ({
  open,
  loading,
  allowCancel = true,
  items,
  userCity,
  detectedCityId,
  onCancel,
  onSubmit,
}) => {
  const [suggestionCityName, setSuggestionCityName] = useState<string | undefined>(undefined);
  const [suggestionCityId, setSuggestionCityId] = useState<number | null>(null);
  const [availableCityId, setAvailableCityId] = useState<number | null>(null);

  const showSuggestionError = !!suggestionCityName?.length && !suggestionCityId;

  useEffect(() => {
    if (!(open && userCity)) {
      return;
    }

    const availableCityExists = items.find((i) => i.id === userCity.id);

    if (availableCityExists) {
      setAvailableCityId(userCity.id);
    } else {
      setSuggestionCityName(userCity.name);
      setSuggestionCityId(userCity.id);
    }
  }, [open, userCity, items]);

  const changeSuggestion = (value?: string): void => {
    setSuggestionCityName(!value || !value.length ? undefined : value);
    setSuggestionCityId(null);
  };

  const selectSuggestion = (suggestion: IAddressSuggestion): void => {
    setAvailableCityId(null);
    setSuggestionCityName(suggestion.name);
    setSuggestionCityId(suggestion.cityId);
  };

  const selectAvailableCity = (id: number): void => {
    setSuggestionCityName(undefined);
    setSuggestionCityId(null);
    setAvailableCityId(id);
  };

  const handleCancel = (): void => {
    if (loading) {
      return;
    }

    setSuggestionCityName(undefined);
    setSuggestionCityId(null);
    setAvailableCityId(null);
    onCancel();
  };

  const handleSubmit = (): void => {
    const cityId = availableCityId ?? suggestionCityId;

    if (!cityId) {
      return;
    }

    onSubmit(cityId, handleCancel);
  };

  return (
    <Modal
      rootClassName="redesign modal"
      title="Выберите свой город"
      styles={{ header: { marginTop: 0 } }}
      width={560}
      centered
      open={open}
      destroyOnClose
      onCancel={allowCancel ? handleCancel : undefined}
      closable={allowCancel}
      footer={[
        allowCancel ? (
          <Button key="cancel" className="button-sm secondary" onClick={handleCancel} loading={loading}>
            Отмена
          </Button>
        ) : null,
        <Button
          key="submit"
          className="button-sm primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!availableCityId && !suggestionCityId}
        >
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-3">
        <AddressSuggestionSelector
          placeholder="Населенный пункт или город"
          value={suggestionCityName}
          onChange={changeSuggestion}
          onSelect={selectSuggestion}
        />
        {showSuggestionError && (
          <div className="ff-regular fs-xs color-red">Продолжайте ввод — нужно выбрать конкретный адрес.</div>
        )}
      </div>

      <div className="scrollbar-none select-city-modal-list">
        {items.map((item, index, array) => (
          <div key={item.id}>
            <ListItem
              item={item}
              isSelected={item.id === availableCityId}
              isDetected={item.id === detectedCityId}
              onSelect={selectAvailableCity}
            />

            <Divider show={index !== array.length - 1} marginY={8} />
          </div>
        ))}

        {!items.length && <span className="ff-regular fs-m color-dark-grey">Список городов пуст.</span>}
      </div>
    </Modal>
  );
};
